import { useEditor, Element } from "@craftjs/core";
import { getRandomId as getRandomNodeId, ROOT_NODE } from "@craftjs/utils";

import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { fromEntries, isJsonString, remove_prefix } from "../../helpers";
import { GrigoraEditorContext } from "../context";
import {
  blockCategories,
  blockCategoryMapping,
  blocksLibrary,
} from "./../../blocks";
import { Panel } from "./../../input";
import "./toolbox.scss";
import _ from "lodash";
import { waitForElement } from "../../dom";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { addNodeTree, getCloneTree } from "../component-blocks";
import { Component } from "../../../assets/ui-icons";

const PanelSection = ({ title, searchBlockString, categorySlug, children }) => {
  // // Using use callback to avoid re-rendering of the component
  const filteredBlocks = useMemo(() => {
    return (
      blockCategoryMapping[categorySlug]?.filter((slug) => {
        // if is hidden from toolbar
        if (blocksLibrary[slug].craft.related.hideFromToolbox) {
          return false;
        }

        if (blocksLibrary[slug].craft?.related?.keywords) {
          return Object.values(
            blocksLibrary[slug].craft?.related?.keywords,
          ).some((item) =>
            item.toLowerCase().includes(searchBlockString?.toLowerCase()),
          );
        } else {
          return blocksLibrary[slug].craft?.name
            ?.toLowerCase()
            ?.includes(searchBlockString?.toLowerCase());
        }
      }) ?? []
    );
  }, [searchBlockString]);

  if (filteredBlocks.length === 0 && !children) {
    return <></>;
  }

  return (
    <div className="panel-section flex flex-col gap-3">
      <h3 className="panel-section__title text-grigora-base font-medium">
        {title}
      </h3>
      <div className="panel-section__content flex flex-wrap gap-2 gap-y-1">
        {filteredBlocks?.map((blockSlug) => {
          return (
            <BlockCard
              key={`${blockSlug}`}
              slug={blockSlug}
              name={blocksLibrary[blockSlug].craft.name}
              Renderer={blocksLibrary[blockSlug]}
              icon={blocksLibrary[blockSlug].craft.related.icon}
              isCanvas={
                blocksLibrary[blockSlug].craft.related.supportNestedBlocks
              }
            />
          );
        })}
        {children}
      </div>
    </div>
  );
};

const BlockCard = ({
  icon,
  Renderer,
  name,
  slug,
  isCanvas,
  searchBlockString,
  ...props
}) => {
  const {
    connectors,
    query,
    query: { node, parseFreshNode },
    actions: { add, selectNode },
  } = useEditor((state) => ({}));

  const { editorDocument = document } = useContext(GrigoraDashboardContext);

  const { isAddingBlocks, setIsAddingBlocks } =
    useContext(GrigoraEditorContext);

  const addNewNode = (e) => {
    var selected = query.getEvent("selected");
    const nodeID = getRandomNodeId();
    const freshNode = {
      id: nodeID,
      data: {
        type: Renderer,
        props: {},
        isCanvas: isCanvas,
      },
    };
    const nodeToAdd = parseFreshNode(freshNode).toNode();

    var node_parent = ROOT_NODE;
    var node_index = undefined;

    const selectedNodeID = selected.first() ?? ROOT_NODE;

    if (selectedNodeID) {
      if (node(selectedNodeID).isCanvas()) {
        node_parent = selectedNodeID;
        node_index = node(selectedNodeID).childNodes().length;
      } else {
        const parents = node(selectedNodeID).ancestors();

        if (parents && parents.length > 0) {
          node_parent = parents[0];
        }
        if (node_parent) {
          let parentChildrens = node(node_parent).childNodes();
          node_index = parentChildrens.indexOf(selectedNodeID) + 1;
        }
      }

      // Check if addition is allowed in selected nodes
      if (
        slug &&
        blocksLibrary[node(node_parent).get()?.data?.custom?.slug] &&
        blocksLibrary[node(node_parent).get()?.data?.custom?.slug]?.craft?.rules
          ?.canMoveIn &&
        !blocksLibrary[
          node(node_parent).get()?.data?.custom?.slug
        ].craft.rules.canMoveIn([nodeToAdd])
      ) {
        return;
      }

      add(nodeToAdd, node_parent, node_index);
      selectNode(nodeID);
      waitForElement(
        editorDocument,
        `[data-grigora-block-id='${nodeID}']`,
      ).then((elm) => {
        elm?.querySelector(".grigora-richtext-input__field")?.focus();
        elm?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      });
      setIsAddingBlocks(false);
    }
  };

  return (
    <div
      ref={(ref) =>
        connectors.create(
          ref,
          isCanvas ? <Element canvas is={Renderer} /> : <Renderer />,
          {
            onCreate: (nodeTree) => {
              selectNode(nodeTree.rootNodeId);
            },
          },
        )
      }
      className="block-card hover:bg-gray-200 bg-white flex w-[47%] p-[12px] py-[12px] gap-3 bg-editor rounded-lg items-center duration-200 cursor-grab "
      onClick={addNewNode}
      onDragStart={(e) => {
        e.target.style.display = "flex";
        document.querySelector(".helper-container").style.pointerEvents =
          "auto";
      }}
      onDragEnd={(e) => {
        setIsAddingBlocks(false);
      }}
    >
      <div className="w-[18px] h-[18px]">{icon}</div>
      <p className="text-grigora-base text-[rgb(14,14,14)] truncate">{name}</p>
    </div>
  );
};

const ComponentCard = ({ componentID, ...props }) => {
  const {
    connectors,
    query,
    query: { node, parseFreshNode },
    actions,
    actions: { add, selectNode },
  } = useEditor((state) => ({}));

  const { isAddingBlocks, setIsAddingBlocks } =
    useContext(GrigoraEditorContext);

  // add templates where you want
  const handleAdd = useCallback(() => {
    // get the template from your database
    if (!isJsonString(props.rendererContent)) {
      return <></>;
    }
    const data = JSON.parse(props.rendererContent);
    const newNodes = JSON.parse(data.nodes);
    const nodePairs = Object.keys(newNodes).map((id) => {
      let nodeId = id;

      return [
        nodeId,
        query
          .parseSerializedNode(newNodes[id])
          .toNode((node) => (node.id = nodeId)),
      ];
    });
    var tree = { rootNodeId: data.rootNodeId, nodes: fromEntries(nodePairs) };

    // Clone IDs
    tree = getCloneTree(tree, query);

    // Update container prop
    if (tree.rootNodeId && tree.nodes && tree.nodes[tree.rootNodeId]) {
      tree.nodes[tree.rootNodeId].data.props.isComponent = true;
      tree.nodes[tree.rootNodeId].data.props.componentID = remove_prefix(
        componentID,
        "component-",
      );
    }

    return tree;
  }, [props.rendererContent, componentID]);

  return (
    <div
      ref={
        isJsonString(props.rendererContent)
          ? (ref) =>
              connectors.create(ref, handleAdd, {
                onCreate: (nodeTree) => {
                  selectNode(nodeTree.rootNodeId);
                },
              })
          : null
      }
      onClick={(e) => {
        var tree = handleAdd();

        var selected = query.getEvent("selected");

        var node_parent = ROOT_NODE;
        var node_index = undefined;

        const selectedNodeID = selected.first() ?? ROOT_NODE;
        if (selectedNodeID) {
          if (node(selectedNodeID).isCanvas()) {
            node_parent = selectedNodeID;
            node_index = node(selectedNodeID).childNodes().length;
          } else {
            const parents = node(selectedNodeID).ancestors();

            if (parents && parents.length > 0) {
              node_parent = parents[0];
            }
            if (node_parent) {
              let parentChildrens = node(node_parent).childNodes();
              node_index = parentChildrens.indexOf(selectedNodeID) + 1;
            }
          }
          // Check if addition is allowed in selected nodes
          if (
            "grigora/container" &&
            blocksLibrary[node(node_parent).get()?.data?.custom?.slug] &&
            blocksLibrary[node(node_parent).get()?.data?.custom?.slug]?.craft
              ?.rules?.canMoveIn &&
            !blocksLibrary[
              node(node_parent).get()?.data?.custom?.slug
            ].craft.rules.canMoveIn([tree.nodes[tree.rootNodeId]])
          ) {
            return;
          }
          actions.addNodeTree(tree, node_parent, node_index);
          actions.selectNode(tree.rootNodeId);
          setIsAddingBlocks(false);
        }
      }}
      onDragStart={(e) => {
        e.target.style.display = "flex";
        document.querySelector(".helper-container").style.pointerEvents =
          "auto";
      }}
      onDragEnd={(e) => {
        setIsAddingBlocks(false);
      }}
      className="block-card hover:bg-gray-200 flex w-[48%] p-[12px] py-[12px] gap-3 bg-editor rounded-lg items-center duration-200 cursor-grab "
    >
      <div className="w-[18px] h-[18px]">
        <Component />
      </div>
      <p className="text-grigora-base text-[rgb(14,14,14)] truncate">
        {props.name}
      </p>
    </div>
  );
};

const Toolbox = () => {
  const { projectComponents } = useContext(GrigoraEditorContext);
  const [searchBlockString, setSearchBlockString] = useState("");

  const searchInputRef = useRef(null);

  // Auto focus search field
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  return (
    <div className="px-[20px] py-[25px] pt-[10px] flex flex-col gap-6">
      <input
        ref={searchInputRef}
        value={searchBlockString}
        onChange={(e) => {
          setSearchBlockString(e.target.value);
        }}
        type="search"
        className="block-search-field focus:outline-none text-grigora-base w-full p-3 bg-gray-100 rounded-[3px]"
        placeholder="Search block..."
      />
      {_.orderBy(blockCategories, ["priority"], ["desc"]).map(
        (blockCategory) => {
          return (
            <PanelSection
              key={`toolbox-category-${blockCategory.slug}`}
              categorySlug={blockCategory.slug}
              title={blockCategory.label}
              initialOpen={blockCategory.expanded}
              searchBlockString={searchBlockString}
            ></PanelSection>
          );
        },
      )}
      {projectComponents && Object.keys(projectComponents).length > 0 && (
        <>
          <PanelSection
            title="Components"
            searchBlockString={searchBlockString}
            initialOpen={false}
          >
            {Object.keys(projectComponents).map((componentID) => {
              return (
                <>
                  <ComponentCard
                    key={componentID}
                    name={projectComponents[componentID].title}
                    componentID={componentID}
                    rendererContent={projectComponents[componentID].content}
                  />
                </>
              );
            })}
          </PanelSection>
        </>
      )}
    </div>
  );
};

export default Toolbox;
